/* eslint-disable functional/immutable-data */
/* eslint-disable react/jsx-one-expression-per-line */
/* eslint-disable max-lines-per-function */
import React, { useState } from 'react'
import {
  Card,
  CardHeader,
  Collapse,
  IconButton,
} from '@mui/material'
import { useHookstate } from '@hookstate/core'
import InternalContent from '~/components/Layout/components/InternalContent'
import { useLang } from '~/hooks/useLang'
import {
  DataGrid,
  type GridColDef,
  type GridRowsProp,
  type GridSortModel,
  gridClasses,
} from '@mui/x-data-grid'
import { ptBR } from '@mui/x-data-grid/locales'
import dayjs from 'dayjs'
import notify from '~/utils/notify'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import VisibilityIcon from '@mui/icons-material/Visibility'
import Tooltip from '@mui/material/Tooltip'
import WorkScheduleLogFilters from './WorkScheduleLogFilters'
import WorkScheduleLogViewModal from '~/features/Audit/components/WorkScheduleLogViewModal'
import { getWorkScheduleLogs } from '../services'
import type { IWorkScheduleLog } from '../interfaces'

const ACTION_LABELS: Record<string, string> = {
  create: 'Create',
  update: 'Update',
  delete: 'Delete',
  print_device_schedule: 'Print Device Schedule',
  print_user_schedule: 'Print User Schedule',
}

const validatePeriod = (
  startDate: string | null,
  finishDate: string | null,
  translateFn: (s: string) => string
) => {
  if (!startDate || !finishDate) {
    notify({
      type: 'error',
      message: translateFn('Start date and end date must be provided'),
    })
    return false
  }
  return true
}

export default function ListAllWorkScheduleLog() {
  const { translate } = useLang()
  const labelButton = useHookstate(translate('Search'))
  const loadingButton = useHookstate(false)
  const expandFilterSearch = useHookstate(true)
  const expandResults = useHookstate(false)
  const userId = useHookstate<number | null>(null)
  const workScheduleId = useHookstate<number | null>(null)
  const action = useHookstate<string | null>(null)
  const resetValues = useHookstate(false)
  const initialPeriod = useHookstate('')
  const [startDate, setStartDate] = useState<string | null>(null)
  const [finishDate, setFinishDate] = useState<string | null>(null)

  const limit = 15
  const [rows, setRows] = useState<GridRowsProp>([])
  const [rowCount, setRowCount] = useState(0)
  const [paginationModel, setPaginationModel] = useState<{
    readonly page: number
    readonly pageSize: number
  }>({ page: 0, pageSize: limit })
  const [sortModel, setSortModel] = useState<GridSortModel>([
    { field: 'created_at', sort: 'desc' },
  ])
  const tableMode = 'server'

  const [openModal, setOpenModal] = useState(false)
  const [selectedLog, setSelectedLog] = useState<IWorkScheduleLog | null>(null)

  const handleView = (log: IWorkScheduleLog) => {
    setSelectedLog(log)
    setOpenModal(true)
  }

  const columns: readonly GridColDef[] = [
    {
      field: 'user_representation',
      headerName: translate('User'),
      flex: 1,
      filterable: false,
      sortable: false,
      disableColumnMenu: true,
    },
    {
      field: 'action',
      headerName: translate('action'),
      flex: 1,
      filterable: false,
      sortable: false,
      disableColumnMenu: true,
      valueFormatter: (value: string) =>
        value ? translate(ACTION_LABELS[value] ?? value) : '-',
    },
    {
      field: 'station_name',
      headerName: translate('Station'),
      flex: 1,
      filterable: false,
      sortable: false,
      disableColumnMenu: true,
    },
    {
      field: 'created_at',
      headerName: translate('Performed at'),
      type: 'date',
      flex: 1,
      filterable: false,
      sortable: true,
      disableColumnMenu: false,
      valueFormatter: (value: string) =>
        value ? dayjs(value).format('DD/MM/YYYY HH:mm') : '-',
    },
    {
      field: 'actions',
      headerName: '',
      sortable: false,
      filterable: false,
      align: 'center',
      width: 80,
      renderCell: (params) => (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
          }}
        >
          <Tooltip title={translate('View Details')}>
            <IconButton
              onClick={() => handleView(params.row as IWorkScheduleLog)}
              size="small"
              aria-label={translate('View Details')}
            >
              <VisibilityIcon />
            </IconButton>
          </Tooltip>
        </div>
      ),
    },
  ]

  const clearFilters = () => {
    setStartDate(null)
    setFinishDate(null)
    initialPeriod.set('')
    userId.set(null)
    workScheduleId.set(null)
    action.set(null)
    resetValues.set(!resetValues.get())
    setRows([])
    setSortModel([{ field: 'created_at', sort: 'desc' }])
    setPaginationModel((prev) => ({ ...prev, page: 0 }))
    expandResults.set(false)
  }

  const mapToRows = (data: readonly IWorkScheduleLog[]): GridRowsProp =>
    data.map((item) => ({
      ...item,
      user_representation: item.user?.representation_name ?? '-',
      station_name: item.station?.name ?? '-',
      work_schedule_name: item.work_schedule?.name ?? '-',
    }))

  const submit = async (
    model = paginationModel,
    currentSortModel: GridSortModel = sortModel
  ) => {
    if (!validatePeriod(startDate, finishDate, translate)) return false

    loadingButton.set(true)

    const orders = currentSortModel.map(({ field, sort }) => ({
      field,
      sort: sort ?? 'desc',
    }))

    try {
      const response = await getWorkScheduleLogs({
        work_schedule_id: workScheduleId.get(),
        user_id: userId.get(),
        action: action.get(),
        start_date: startDate as string,
        finish_date: finishDate as string,
        orders: orders.length > 0 ? orders : [{ field: 'created_at', sort: 'desc' }],
        page: model.page,
        limit: model.pageSize,
      })

      setRows(mapToRows(response.data))
      setRowCount(response.rowsCount)
      expandResults.set(true)
    } catch (error) {
      notify({ type: 'error', message: translate('Error loading data') })
    } finally {
      loadingButton.set(false)
    }
  }

  const searchFromFirstPage = () => {
    const firstPageModel = { page: 0, pageSize: paginationModel.pageSize }
    setPaginationModel(firstPageModel)
    submit(firstPageModel, sortModel)
  }

  return (
    <>
      <InternalContent
        title={translate('Filters')}
        sxFont={{ fontWeight: 'bold', fontSize: 18 }}
      >
        <WorkScheduleLogFilters
          hidden={expandFilterSearch.get()}
          loadingButton={loadingButton}
          labelButton={labelButton}
          resetValues={resetValues}
          userId={userId}
          workScheduleId={workScheduleId}
          action={action}
          initialPeriod={initialPeriod}
          onPeriodChange={(value) => {
            labelButton.set(translate('Search'))
            initialPeriod.set(value)
          }}
          onStartDateChange={(value) => {
            labelButton.set(translate('Search'))
            setStartDate(value)
          }}
          onFinishDateChange={(value) => {
            labelButton.set(translate('Search'))
            setFinishDate(value)
          }}
          clearFilters={clearFilters}
          searchButton={searchFromFirstPage}
        />
      </InternalContent>

      <Card sx={{ boxShadow: 4, marginBottom: 2 }}>
        <CardHeader
          title={translate('Actions Performed')}
          avatar={
            <IconButton
              onClick={() => expandResults.set(!expandResults.get())}
              sx={{
                display: 'flex',
                justifyContent: 'end',
                marginRight: 1,
                padding: 0,
              }}
            >
              {expandResults.value ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          }
          titleTypographyProps={{ fontWeight: 'bold', fontSize: 18 }}
        />
        <Collapse in={expandResults.value}>
          <DataGrid
            localeText={ptBR.components.MuiDataGrid.defaultProps.localeText}
            rows={rows}
            columns={columns}
            loading={loadingButton.value}
            paginationMode={tableMode}
            paginationModel={paginationModel}
            onPaginationModelChange={(model) => {
              setPaginationModel(model)
              submit(model, sortModel)
            }}
            sortingMode={tableMode}
            sortModel={sortModel}
            onSortModelChange={(model) => {
              setSortModel(model)
              const firstPageModel = { page: 0, pageSize: paginationModel.pageSize }
              setPaginationModel(firstPageModel)
              submit(firstPageModel, model)
            }}
            disableColumnFilter
            disableColumnSelector
            autoHeight
            pagination
            filterMode={tableMode}
            rowCount={rowCount}
            getRowId={(row) => Number(row.id)}
            sx={{
              [`& .${gridClasses.cell}`]: {
                py: 1,
              },
              minHeight: '250px',
              margin: 4,
              [`.${gridClasses.columnHeaderTitle}`]: {
                fontWeight: 'bold !important',
              },
            }}
          />
        </Collapse>
      </Card>

      <WorkScheduleLogViewModal
        open={openModal}
        onClose={() => setOpenModal(false)}
        log={selectedLog}
      />
    </>
  )
}
